import { llmSettingsManager } from '@/IsekaiRPG/modules/llm/llmSettingsPersistence';
import {
  type LLMConfig,
  type LLMGenerateOptions,
  type LLMMessage,
  type RegexRule,
  type LLMStreamHandlers,
} from '@/IsekaiRPG/modules/llm/llmTypes';
import { LLM_DEBUG_CONFIG } from '@/IsekaiRPG/modules/llm/llmDebugConfig';
import { LLMEmptyResponseError, LLMUpstreamError } from '@/IsekaiRPG/modules/llm/llmErrors';
import { MessagePipeline, type MessagePipelineChainTrace } from '@/IsekaiRPG/modules/llm/llmMessagePipeline';
import { OpenAICompatibleClient } from '@/IsekaiRPG/modules/llm/llmOpenAICompatibleClient';
import { RegexProcessor } from '@/IsekaiRPG/modules/llm/llmRegexProcessor';
import { STPromptCompiler, type STPromptCompilerTrace } from '@/IsekaiRPG/modules/llm/llmSTPromptCompiler';
import { LLMTracingService } from '@/IsekaiRPG/modules/llm/llmTracingService';
import { LogService } from '@/IsekaiRPG/shared/logging/logService';
import { klona } from 'klona';
import {
  GardenFlowerResolver,
  type GardenFlowerSnapshotMeta,
} from './garden/GardenFlowerResolver';

export interface PromptCompilePreviewSnapshot {
  timestamp: number;
  generationType: string;
  runtimeMessages: LLMMessage[];
  compiledMessages: LLMMessage[];
  trace: STPromptCompilerTrace;
  gardenFlower: GardenFlowerSnapshotMeta | null;
  isDryRun?: boolean;
}

export interface MessagePipelinePreviewSnapshot {
  timestamp: number;
  requestId: string;
  generationType: string;
  useSystemPrompt: boolean;
  runtime: MessagePipelineChainTrace;
  compiled: MessagePipelineChainTrace;
}

// ============================================
// LLM 提供器
// ============================================

export class LLMProvider {
  private static readonly TAG = 'LLMProvider';
  private static config: LLMConfig | null = null;
  private static initialized = false;
  private static initPromise: Promise<LLMConfig> | null = null;
  private static lastPromptCompilePreview: PromptCompilePreviewSnapshot | null = null;
  private static lastMessagePipelinePreview: MessagePipelinePreviewSnapshot | null = null;

  /**
   * 初始化配置
   */
  static async init(): Promise<LLMConfig> {
    if (this.initialized) {
      return klona(this.getRequiredConfig());
    }

    if (!this.initPromise) {
      this.initPromise = (async () => {
        const loadedConfig = await llmSettingsManager.load();

        // 挂载后预热时可能与面板侧的运行时同步并发发生；若期间已有更新，则不再回写旧配置。
        if (!this.initialized) {
          this.config = loadedConfig;
          this.initialized = true;
          LogService.info('LLMProvider', 'LLM 配置初始化完成');
        }

        return klona(this.getRequiredConfig());
      })().finally(() => {
        this.initPromise = null;
      });
    }

    return this.initPromise;
  }

  /**
   * 获取当前配置
   */
  static getConfig(): LLMConfig {
    return klona(this.getRequiredConfig());
  }

  /**
   * 立即用当前内存配置覆盖 Provider 运行时配置。
   * 用于“用户刚改完预设/世界书就立刻发送”的场景，避免异步持久化尚未完成时仍使用旧配置。
   */
  static syncRuntimeConfig(config: LLMConfig): void {
    this.config = klona(config);
    this.initialized = true;
  }

  /**
   * 更新配置
   */
  static async updateConfig(partial: Partial<LLMConfig>): Promise<LLMConfig> {
    if (!this.initialized) {
      await this.init();
    }

    this.config = await llmSettingsManager.update(partial);
    LogService.info('LLMProvider', 'LLM 配置已更新');
    return klona(this.getRequiredConfig());
  }

  /**
   * 仅更新 API Key
   */
  static async setApiKey(apiKey: string): Promise<void> {
    await this.updateConfig({ apiKey });
  }

  /**
   * 获取最近一次提示词编译快照
   */
  static getLastPromptCompilePreview(): PromptCompilePreviewSnapshot | null {
    return this.lastPromptCompilePreview ? klona(this.lastPromptCompilePreview) : null;
  }

  static getLastMessagePipelinePreview(): MessagePipelinePreviewSnapshot | null {
    return this.lastMessagePipelinePreview ? klona(this.lastMessagePipelinePreview) : null;
  }

  /**
   * 仅编译提示词预览而不发送请求
   * 用于 UI 预览功能，让用户在不消耗 Token 的情况下查看当前的提示词预设和世界书命中情况。
   */
  static compileOnly(options: LLMGenerateOptions): PromptCompilePreviewSnapshot {
    const requestId = options.request_id?.trim() || `preview_${Date.now().toString(36)}`;
    const config = this.getRequiredConfig();
    const previousSnapshot = this.getLastPromptCompilePreview();

    this.compileMessagesWithPreset(config, klona(options), requestId);
    const snapshot = this.getLastPromptCompilePreview()!;
    snapshot.isDryRun = true;
    this.lastPromptCompilePreview = previousSnapshot;
    return snapshot;
  }

  /**
   * 调用 LLM
   */
  static async generate(options: LLMGenerateOptions): Promise<string> {
    const requestId = options.request_id?.trim() || this.createRequestId();
    const generateStartedAt = Date.now();

    if (!this.initialized) {
      await this.init();
    }

    const config = this.getRequiredConfig();
    const requestConfig = this.resolveRequestConfig(config, options);

    if (!requestConfig.apiKey) {
      throw new Error('LLM API Key 未配置，请先由玩家在设置中填写');
    }

    LLMTracingService.logGenerateStart(requestId, requestConfig.provider, options.generation_type ?? 'normal', false);

    const runtimeMessages = MessagePipeline.normalizeMessages(options);
    if (LLM_DEBUG_CONFIG.PROMPT_MESSAGES_LOG_ENABLED) {
      LLMTracingService.logPromptMessages('发送前（运行时）', runtimeMessages, requestId);
    }

    const compileStartedAt = Date.now();
    const compiledOptions = this.compileMessagesWithPreset(config, options, requestId);
    const normalizedOptions = this.normalizeStreamingOption(
      requestConfig.provider,
      compiledOptions,
      requestId,
      'normal',
    );
    this.captureAndLogMessagePipelinePreview(requestId, requestConfig, options, normalizedOptions);
    const compileMs = Date.now() - compileStartedAt;
    if (LLM_DEBUG_CONFIG.PROMPT_MESSAGES_LOG_ENABLED) {
      LLMTracingService.logPromptMessages(
        '发送前（预设编译后）',
        MessagePipeline.normalizeMessages(normalizedOptions),
        requestId,
      );
    }

    let result = '';

    try {
      result = await this.executeProviderRequest(requestConfig, normalizedOptions, requestId);

      const regexProcessedResult = this.applyRegexPostProcess(config, result);

      LLMTracingService.logGenerateDone(
        requestId,
        {
          compileMs,
          totalMs: Date.now() - generateStartedAt,
          outputLength: regexProcessedResult.length,
        },
        false,
      );

      return regexProcessedResult;
    } catch (error) {
      if (this.isAbortError(error)) {
        LogService.info(this.TAG, `[${requestId}] LLM 请求已取消`);
        throw error;
      }
      LLMTracingService.logGenerateError(requestId, error, false);
      if (error instanceof LLMUpstreamError || error instanceof LLMEmptyResponseError) {
        throw error;
      }
      throw new LLMUpstreamError('LLM 请求失败', { requestId, provider: requestConfig.provider }, error);
    }
  }

  /**
   * 流式调用 LLM（统一使用 OpenAI 兼容接口）
   */
  static async generateStream(options: LLMGenerateOptions, handlers: LLMStreamHandlers = {}): Promise<string> {
    const requestId = options.request_id?.trim() || this.createRequestId();
    const generateStartedAt = Date.now();

    if (!this.initialized) {
      await this.init();
    }

    const config = this.getRequiredConfig();
    const requestConfig = this.resolveRequestConfig(config, options);

    if (!requestConfig.apiKey) {
      throw new Error('LLM API Key 未配置，请先由玩家在设置中填写');
    }

    LLMTracingService.logGenerateStart(requestId, requestConfig.provider, options.generation_type ?? 'normal', true);

    const runtimeMessages = MessagePipeline.normalizeMessages(options);
    if (LLM_DEBUG_CONFIG.PROMPT_MESSAGES_LOG_ENABLED) {
      LLMTracingService.logPromptMessages('发送前（运行时）', runtimeMessages, requestId);
    }

    const compileStartedAt = Date.now();
    const compiledOptions = this.compileMessagesWithPreset(config, options, requestId);
    const normalizedOptions = this.normalizeStreamingOption(
      requestConfig.provider,
      compiledOptions,
      requestId,
      'stream',
    );
    this.captureAndLogMessagePipelinePreview(requestId, requestConfig, options, normalizedOptions);
    const compileMs = Date.now() - compileStartedAt;
    if (LLM_DEBUG_CONFIG.PROMPT_MESSAGES_LOG_ENABLED) {
      LLMTracingService.logPromptMessages(
        '发送前（预设编译后）',
        MessagePipeline.normalizeMessages(normalizedOptions),
        requestId,
      );
    }

    try {
      const result = await this.executeProviderStreamRequest(requestConfig, normalizedOptions, requestId, handlers);
      const regexProcessedResult = this.applyRegexPostProcess(config, result);
      LLMTracingService.logGenerateDone(
        requestId,
        {
          compileMs,
          totalMs: Date.now() - generateStartedAt,
          outputLength: regexProcessedResult.length,
        },
        true,
      );

      try {
        handlers.onDone?.(regexProcessedResult);
      } catch (callbackError) {
        LogService.error(this.TAG, `[${requestId}] onDone 回调异常`, callbackError as Error);
      }

      return regexProcessedResult;
    } catch (error) {
      if (this.isAbortError(error)) {
        LogService.info(this.TAG, `[${requestId}] LLM 流式请求已取消`);
        throw error;
      }
      LLMTracingService.logGenerateError(requestId, error, true);
      try {
        handlers.onError?.(error);
      } catch (callbackError) {
        LogService.error(this.TAG, `[${requestId}] onError 回调异常`, callbackError as Error);
      }
      if (error instanceof LLMUpstreamError || error instanceof LLMEmptyResponseError) {
        throw error;
      }
      throw new LLMUpstreamError('LLM 流式请求失败', { requestId, provider: requestConfig.provider }, error);
    }
  }

  // ============================================
  // 内部实现
  // ============================================

  private static async executeProviderRequest(
    config: LLMConfig,
    options: LLMGenerateOptions,
    requestId: string,
  ): Promise<string> {
    return this.generateByOpenAICompatible(config, options, requestId);
  }

  private static async executeProviderStreamRequest(
    config: LLMConfig,
    options: LLMGenerateOptions,
    requestId: string,
    handlers: LLMStreamHandlers,
  ): Promise<string> {
    if (options.should_stream) {
      return this.generateByOpenAICompatibleStream(config, options, requestId, handlers);
    }

    // 非流式 fallback（保持接口一致）
    const text = await this.executeProviderRequest(
      config,
      {
        ...options,
        should_stream: false,
      },
      requestId,
    );
    handlers.onChunk?.(text, text);
    return text;
  }

  /**
   * OpenAI 兼容 Chat Completions 接口调用
   */
  private static async generateByOpenAICompatible(
    config: LLMConfig,
    options: LLMGenerateOptions,
    requestId: string,
  ): Promise<string> {
    const baseUrl = config.baseUrl.replace(/\/+$/, '');
    const endpoint = `${baseUrl}/chat/completions`;
    const generationParams = this.resolveGenerationParams(config, options);
    const normalizedMessages = MessagePipeline.normalizeMessages(options);
    if (normalizedMessages.length === 0) {
      throw new Error('LLM 请求缺少可发送的消息');
    }
    return OpenAICompatibleClient.generate({
      endpoint,
      apiKey: config.apiKey,
      model: config.model,
      messages: normalizedMessages,
      maxTokens: generationParams.maxTokens ?? undefined,
      temperature: generationParams.temperature,
      topP: generationParams.topP,
      topK: generationParams.topK,
      topA: generationParams.topA,
      minP: generationParams.minP,
      repetitionPenalty: generationParams.repetitionPenalty,
      presencePenalty: generationParams.presencePenalty,
      frequencyPenalty: generationParams.frequencyPenalty,
      stream: options.should_stream ?? false,
      requestId,
      signal: options.signal,
    });
  }

  /**
   * OpenAI 兼容 Chat Completions 流式调用（SSE）
   */
  private static async generateByOpenAICompatibleStream(
    config: LLMConfig,
    options: LLMGenerateOptions,
    requestId: string,
    handlers: LLMStreamHandlers,
  ): Promise<string> {
    const baseUrl = config.baseUrl.replace(/\/+$/, '');
    const endpoint = `${baseUrl}/chat/completions`;
    const generationParams = this.resolveGenerationParams(config, options);
    const normalizedMessages = MessagePipeline.normalizeMessages(options);
    if (normalizedMessages.length === 0) {
      throw new Error('LLM 请求缺少可发送的消息');
    }
    return OpenAICompatibleClient.generateStream(
      {
        endpoint,
        apiKey: config.apiKey,
        model: config.model,
        messages: normalizedMessages,
        maxTokens: generationParams.maxTokens ?? undefined,
        temperature: generationParams.temperature,
        topP: generationParams.topP,
        topK: generationParams.topK,
        topA: generationParams.topA,
        minP: generationParams.minP,
        repetitionPenalty: generationParams.repetitionPenalty,
        presencePenalty: generationParams.presencePenalty,
        frequencyPenalty: generationParams.frequencyPenalty,
        stream: true,
        requestId,
        signal: options.signal,
      },
      handlers,
    );
  }

  // ============================================
  // 消息编译与转换
  // ============================================

  private static compileMessagesWithPreset(
    config: LLMConfig,
    options: LLMGenerateOptions,
    requestId: string,
  ): LLMGenerateOptions {
    const macroContext = this.buildMacroContext(options);
    const activeRegexRules = this.getActiveRegexRules(config);
    const runtimeMessages = this.applyRegexToRuntimeMessages(
      MessagePipeline.normalizeMessages(options),
      activeRegexRules,
      {
        macroContext,
      },
    );
    const shouldApplyPreset = options.apply_st_preset ?? true;
    const generationType = (options.generation_type ?? 'normal').trim().toLowerCase();
    const runtimeWorldBook = options.world_book ?? config.worldBook;

    if (!shouldApplyPreset) {
      this.setLastPromptCompilePreview({
        timestamp: Date.now(),
        generationType,
        runtimeMessages,
        compiledMessages: runtimeMessages,
        gardenFlower: this.resolveGardenFlowerPreview(runtimeMessages, options.marker_payloads),
        trace: {
          generationType,
          worldBookEnabled: !!runtimeWorldBook?.enabled,
          matchedCount: 0,
          totalEntries: runtimeWorldBook?.entries?.length ?? 0,
          entries: [],
        },
      });
      return {
        ...options,
        messages: runtimeMessages,
      };
    }

    const compiled = STPromptCompiler.compileWithTrace({
      preset: config.stPreset,
      worldBook: runtimeWorldBook,
      runtimeMessages,
      markerPayloads: options.marker_payloads,
      mainPromptOverride: options.main_prompt_override,
      macroContext,
      characterContext: {
        names: options.character_context?.names ?? [],
        tags: options.character_context?.tags ?? [],
      },
      generationType: options.generation_type,
      customPromptPostProcessing: options.custom_prompt_post_processing,
      regexRules: activeRegexRules,
    });
    if (LLM_DEBUG_CONFIG.PROMPT_COMPILE_TRACE_LOG_ENABLED) {
      LLMTracingService.logPromptCompileTrace(compiled.trace, requestId);
    }
    this.setLastPromptCompilePreview({
      timestamp: Date.now(),
      generationType: compiled.trace.generationType,
      runtimeMessages,
      compiledMessages: compiled.messages,
      gardenFlower: this.resolveGardenFlowerPreview(runtimeMessages, options.marker_payloads),
      trace: compiled.trace,
    });

    return {
      ...options,
      messages: compiled.messages,
    };
  }

  private static normalizeStreamingOption(
    provider: LLMConfig['provider'],
    options: LLMGenerateOptions,
    requestId: string,
    mode: 'normal' | 'stream',
  ): LLMGenerateOptions {
    if (!options.should_stream) {
      return options;
    }

    if (mode === 'stream') {
      return options;
    }

    LLMTracingService.logStreamingDowngrade(requestId, {
      provider,
      requestedStream: true,
      mode,
    });

    return {
      ...options,
      should_stream: false,
    };
  }

  private static resolveRequestConfig(config: LLMConfig, options: LLMGenerateOptions): LLMConfig {
    if (options.runtimeModelProfile !== 'lightweight') {
      return config;
    }

    return {
      ...config,
      baseUrl: config.lightweightBaseUrl.trim() || config.baseUrl,
      model: config.lightweightModel.trim() || config.model,
      apiKey: config.lightweightApiKey.trim() || config.apiKey,
    };
  }

  private static resolveGardenFlowerPreview(
    runtimeMessages: LLMMessage[],
    markerPayloads?: Record<string, string[]>,
  ): GardenFlowerSnapshotMeta | null {
    return (
      GardenFlowerResolver.detectFromMessages(runtimeMessages, { includeProtocolOnly: true }) ??
      GardenFlowerResolver.detectFromMarkerPayloads(markerPayloads, { includeProtocolOnly: true })
    );
  }

  private static isAbortError(error: unknown): boolean {
    return error instanceof Error && error.name === 'AbortError';
  }

  private static captureAndLogMessagePipelinePreview(
    requestId: string,
    config: LLMConfig,
    originalOptions: LLMGenerateOptions,
    normalizedOptions: LLMGenerateOptions,
  ): void {
    const useSystemPrompt = normalizedOptions.use_sysprompt ?? config.stPreset.use_sysprompt;
    const runtimeTrace = MessagePipeline.buildChainTrace(originalOptions, useSystemPrompt);
    const compiledTrace = MessagePipeline.buildChainTrace(normalizedOptions, useSystemPrompt);

    this.lastMessagePipelinePreview = {
      timestamp: Date.now(),
      requestId,
      generationType: (normalizedOptions.generation_type ?? 'normal').trim().toLowerCase(),
      useSystemPrompt,
      runtime: runtimeTrace,
      compiled: compiledTrace,
    };

    if (!LLM_DEBUG_CONFIG.MESSAGE_PIPELINE_TRACE_LOG_ENABLED) return;
    if (LLM_DEBUG_CONFIG.MESSAGE_PIPELINE_TRACE_RUNTIME_LOG_ENABLED) {
      LLMTracingService.logMessagePipelineTrace('运行时', runtimeTrace, requestId);
    }
    LLMTracingService.logMessagePipelineTrace('预设编译后', compiledTrace, requestId);
  }

  private static setLastPromptCompilePreview(snapshot: PromptCompilePreviewSnapshot): void {
    this.lastPromptCompilePreview = klona(snapshot);
  }

  private static buildMacroContext(options: LLMGenerateOptions): Record<string, string> {
    const context: Record<string, string> = {};

    for (const [key, value] of Object.entries(options.macro_context ?? {})) {
      const normalizedKey = key.trim().toLowerCase();
      const normalizedValue = String(value ?? '').trim();
      if (!normalizedKey || !normalizedValue) continue;
      context[normalizedKey] = normalizedValue;
    }

    return context;
  }

  private static applyRegexPostProcess(config: LLMConfig, text: string): string {
    const mergedRules = this.getActiveRegexRules(config);
    return RegexProcessor.applyForPlacement(text, mergedRules, 2);
  }

  private static resolveGenerationParams(
    config: LLMConfig,
    options: LLMGenerateOptions,
  ): {
    maxTokens: number | null;
    temperature: number | null;
    topP: number | null;
    topK: number | null;
    topA: number | null;
    minP: number | null;
    repetitionPenalty: number | null;
    presencePenalty: number | null;
    frequencyPenalty: number | null;
  } {
    const shouldSendExtendedSamplingParams = config.sendExtendedSamplingParams;
    return {
      maxTokens: options.max_tokens ?? config.stPreset.maxTokens ?? config.maxTokens ?? null,
      temperature: options.temperature ?? config.stPreset.temperature ?? config.temperature ?? null,
      topP: options.top_p ?? config.stPreset.topP ?? config.topP ?? null,
      topK: shouldSendExtendedSamplingParams ? (options.top_k ?? config.stPreset.topK ?? config.topK ?? null) : null,
      topA: shouldSendExtendedSamplingParams ? (options.top_a ?? config.stPreset.topA ?? config.topA ?? null) : null,
      minP: shouldSendExtendedSamplingParams ? (options.min_p ?? config.stPreset.minP ?? config.minP ?? null) : null,
      repetitionPenalty: shouldSendExtendedSamplingParams
        ? (options.repetition_penalty ?? config.stPreset.repetitionPenalty ?? config.repetitionPenalty ?? null)
        : null,
      presencePenalty: options.presence_penalty ?? config.stPreset.presencePenalty ?? config.presencePenalty ?? null,
      frequencyPenalty:
        options.frequency_penalty ?? config.stPreset.frequencyPenalty ?? config.frequencyPenalty ?? null,
    };
  }

  private static applyRegexToRuntimeMessages(
    messages: LLMMessage[],
    rules: RegexRule[],
    options: { macroContext: Record<string, string> },
  ): LLMMessage[] {
    if (!messages.length || !rules.length) return messages;
    const total = messages.length;
    return messages.map((message, index) => {
      const placement = message.role === 'user' ? 1 : 2;
      const depth = total - index - 1;
      return {
        ...message,
        content: RegexProcessor.applyForPlacement(message.content, rules, placement, {
          isPrompt: true,
          depth,
          macroContext: options.macroContext,
        }),
      };
    });
  }

  private static getActiveRegexRules(config: LLMConfig): RegexRule[] {
    const activeRegexConfig = config.regexConfigs?.find(item => item.id === config.activeRegexConfigId);
    const regexEnabled = activeRegexConfig?.enabled ?? config.regexEnabled;
    if (!regexEnabled) return [];

    if (!activeRegexConfig) {
      return this.normalizeRegexExecutionOrder(config.regexGlobalRules ?? [], []);
    }

    return this.normalizeRegexExecutionOrder(activeRegexConfig.globalRules, activeRegexConfig.presetRules);
  }

  // 对齐 SillyTavern 的块级优先级：全局规则先于预设规则，再分别保留块内顺序。
  private static normalizeRegexExecutionOrder(globalRules: RegexRule[], presetRules: RegexRule[]): RegexRule[] {
    const globalOrdered = this.normalizeRegexScopeOrder(globalRules, 10);
    const presetOrdered = this.normalizeRegexScopeOrder(presetRules, (globalOrdered.length + 1) * 10);
    return [...globalOrdered, ...presetOrdered];
  }

  private static normalizeRegexScopeOrder(rules: RegexRule[], startOrder: number): RegexRule[] {
    return [...rules]
      .sort((a, b) => {
        const orderA = Number.isFinite(a.order) ? a.order : 100;
        const orderB = Number.isFinite(b.order) ? b.order : 100;
        if (orderA !== orderB) return orderA - orderB;
        return a.id.localeCompare(b.id);
      })
      .map((rule, index) => ({
        ...rule,
        order: startOrder + index * 10,
      }));
  }

  private static createRequestId(): string {
    return `llm_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  }

  /**
   * 获取配置（内部断言）
   */
  private static getRequiredConfig(): LLMConfig {
    if (!this.config) {
      throw new Error('LLMProvider 尚未初始化，请先调用 LLMProvider.init()');
    }
    return this.config;
  }
}
